/**
 * Admin Wiki edit surfaces (Slice 2 CP10 Phase 3).
 *
 * Minimal write UI for the three CP10 wiki mutation paths:
 *   - POST  /v2/staging/wiki/entity-pages        — entity wiki page create
 *   - PATCH /v2/staging/wiki/entity-pages/:id    — entity wiki page edit
 *   - PATCH /v2/staging/wiki/arc-summaries/:id   — arc summary_md edit
 *   - PATCH /v2/staging/wiki/challenge-summaries/:id — challenge summary_md edit
 *
 * Scope guardrails (CP10 plan §4.3 + Phase 3 guardrails):
 *   • plain `<textarea>` for body_md / overview_md / summary_md — no rich
 *     text editor, no preview, no toolbar
 *   • no media / source UI, no revision history, no moderation controls
 *   • no public UX drift — this file only adds routes under `/wiki/*`
 *   • `entity_id` on the create form is a stringified bigint text input
 *     (the public entities artifact doesn't expose `id`; a dropdown
 *     would require widening the admin read surface, which is out of
 *     CP10 scope)
 *
 * Idempotency: one `crypto.randomUUID()` per form open, reused across
 * retries so a double-click replays on `mutation_events.idempotency_key`
 * UNIQUE without writing a second audit row.
 *
 * Delta-only PATCH: the entity-page edit form builds a body containing
 * only fields actually changed vs the admin-list row snapshot. The
 * arc / challenge summary edit forms always send `summary_md` in the
 * body (the form has exactly one editable field, so a submit always
 * carries its current value — nullable via the "Clear summary" toggle).
 */

import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type {
  WikiAdminArcRow,
  WikiAdminChallengeRow,
  WikiAdminPageRow,
  WikiArcSummaryEditRequest,
  WikiArcSummaryRowSnapshot,
  WikiChallengeSummaryEditRequest,
  WikiChallengeSummaryRowSnapshot,
  WikiEntityPageCreateRequest,
  WikiEntityPageEditRequest,
  WikiMutationReceiptEnvelope,
  WikiPageRowSnapshot,
  WikiPageStatus,
  WikiValidationError,
} from '@fishstank/contracts'
import {
  createWikiEntityPage,
  editArcSummary,
  editChallengeSummary,
  editWikiEntityPage,
  fetchWikiAdminArcs,
  fetchWikiAdminChallenges,
  fetchWikiAdminList,
  type WikiMutationFetchResult,
} from '../lib/worker'
import { Shell } from '../layouts/Shell'

// ────────────────────────────────────────────────────────────────────────────
// Shared utilities
// ────────────────────────────────────────────────────────────────────────────

function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID()
}

const WIKI_STATUS_OPTIONS: readonly WikiPageStatus[] = [
  'draft',
  'review',
  'published',
  'archived',
]

function validationMessage(err: WikiValidationError): string {
  switch (err.code) {
    case 'invalid_json':
      return 'Request body is not valid JSON.'
    case 'invalid_uuid':
      return `Internal error: ${err.field} must be a UUID. Refresh and try again.`
    case 'idempotency_key_in_use':
      return 'This form was already submitted. Refresh to start a new edit.'
    case 'missing_field':
      return `"${err.field}" is required.`
    case 'invalid_type':
      return `"${err.field}" must be ${err.expected}.`
    case 'invalid_enum':
      return `"${err.field}" is not allowed (expected: ${err.allowed.join(', ')}).`
    case 'field_too_long':
      return `"${err.field}" is too long (max ${err.max} characters).`
    case 'field_too_short':
      return `"${err.field}" is too short (min ${err.min} characters).`
    case 'invalid_slug':
      return 'Slug must be lowercase letters, numbers, and hyphens only.'
    case 'slug_conflict':
      return 'Another wiki page already uses this slug.'
    case 'invalid_entity_id':
      return 'entity_id must be a positive integer.'
    case 'entity_not_found':
      return 'No entity with that id.'
    case 'entity_already_has_page':
      return 'This entity already has a wiki page. Edit the existing one instead.'
    case 'wiki_page_not_found':
      return 'Wiki page not found.'
    case 'empty_patch':
      return 'Nothing changed. Modify at least one field before submitting.'
    case 'db_unavailable':
      return 'Database unavailable — try again in a moment.'
    case 'arc_not_found':
      return 'Arc not found.'
    case 'challenge_not_found':
      return 'Challenge not found.'
    case 'summary_too_long':
      return `Summary is too long (max ${err.max} characters).`
    default:
      return 'Validation failed.'
  }
}

function fieldKey(err: WikiValidationError): string | null {
  if ('field' in err && typeof err.field === 'string') return err.field
  return null
}

function FieldError({ error }: { error: WikiValidationError }) {
  return (
    <span
      data-testid="wiki-field-error"
      data-error-code={error.code}
      className="mt-1 block text-xs text-red-300"
    >
      {validationMessage(error)}
    </span>
  )
}

// Classify a non-ok mutation result into the top-level banner copy we
// render. Validation errors are handled per-field at the call site;
// this helper only formats the non-field-bound states.
function topErrorMessage<T>(
  res: WikiMutationFetchResult<T>,
): string | null {
  if (res.kind === 'ok') return null
  if (res.kind === 'validation_error') return null
  if (res.kind === 'csrf_error') return `CSRF check failed: ${res.reason}`
  if (res.kind === 'not_found') return 'Not found (HTTP 404).'
  return res.message
}

// ────────────────────────────────────────────────────────────────────────────
// Route 1 — POST /wiki/new   → entity wiki page create
// ────────────────────────────────────────────────────────────────────────────

interface CreateEntityPageState {
  title: string
  slug: string
  entity_id: string
  status: WikiPageStatus
  overview_md: string
  body_md: string
  is_developing_override: '' | 'true' | 'false'
}

const EMPTY_CREATE_ENTITY_PAGE: CreateEntityPageState = {
  title: '',
  slug: '',
  entity_id: '',
  status: 'draft',
  overview_md: '',
  body_md: '',
  is_developing_override: '',
}

export function WikiEntityPageCreatePage() {
  const nav = useNavigate()
  const [state, setState] = useState<CreateEntityPageState>(
    () => ({ ...EMPTY_CREATE_ENTITY_PAGE }),
  )
  const [idempotencyKey] = useState<string>(() => newIdempotencyKey())
  const [validationError, setValidationError] =
    useState<WikiValidationError | null>(null)
  const [topLevelError, setTopLevelError] = useState<string | null>(null)

  const mutation = useMutation<
    WikiMutationFetchResult<WikiMutationReceiptEnvelope<WikiPageRowSnapshot>>,
    Error,
    WikiEntityPageCreateRequest
  >({
    mutationFn: (body) => createWikiEntityPage({ body }),
    onSuccess: (res) => {
      if (res.kind === 'ok') {
        nav('/wiki')
      } else if (res.kind === 'validation_error') {
        setValidationError(res.error)
      } else {
        setTopLevelError(topErrorMessage(res))
      }
    },
  })

  const errorField = validationError ? fieldKey(validationError) : null

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setValidationError(null)
    setTopLevelError(null)
    const body: WikiEntityPageCreateRequest = {
      idempotency_key: idempotencyKey,
      title: state.title,
      slug: state.slug,
      entity_id: state.entity_id,
      status: state.status,
      overview_md: state.overview_md.length === 0 ? null : state.overview_md,
      body_md: state.body_md.length === 0 ? null : state.body_md,
      is_developing_override:
        state.is_developing_override === ''
          ? null
          : state.is_developing_override === 'true',
    }
    mutation.mutate(body)
  }

  return (
    <Shell provenance={null}>
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold">New entity wiki page</h1>
          <p className="mt-1 text-sm text-slate-400">
            Create a <code className="text-slate-300">wiki_pages</code> row
            bound to an existing entity. Entity id is set at create time
            only; it can't be changed after.
          </p>
        </div>
        <Link
          to="/wiki"
          data-testid="wiki-create-cancel"
          className="text-xs text-slate-400 hover:text-slate-200"
        >
          Cancel
        </Link>
      </header>
      <form
        onSubmit={submit}
        data-testid="wiki-create-form"
        className="grid grid-cols-2 gap-3 rounded-md border border-fishstank-accent/40 bg-fishstank-surface p-4"
      >
        <label className="col-span-2 text-xs text-slate-300">
          Title
          <input
            type="text"
            data-testid="wiki-create-title"
            value={state.title}
            onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))}
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
          />
          {errorField === 'title' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="text-xs text-slate-300">
          Slug
          <input
            type="text"
            data-testid="wiki-create-slug"
            value={state.slug}
            onChange={(e) => setState((s) => ({ ...s, slug: e.target.value }))}
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100 font-mono"
          />
          {errorField === 'slug' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="text-xs text-slate-300">
          Entity id (stringified bigint)
          <input
            type="text"
            data-testid="wiki-create-entity-id"
            value={state.entity_id}
            onChange={(e) =>
              setState((s) => ({ ...s, entity_id: e.target.value }))
            }
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100 font-mono"
          />
          {errorField === 'entity_id' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="text-xs text-slate-300">
          Status
          <select
            data-testid="wiki-create-status"
            value={state.status}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                status: e.target.value as WikiPageStatus,
              }))
            }
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
          >
            {WIKI_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {errorField === 'status' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="text-xs text-slate-300">
          is_developing_override
          <select
            data-testid="wiki-create-is-developing"
            value={state.is_developing_override}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                is_developing_override:
                  e.target.value as CreateEntityPageState['is_developing_override'],
              }))
            }
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
          >
            <option value="">(auto — null)</option>
            <option value="true">force developing</option>
            <option value="false">force ready</option>
          </select>
        </label>
        <label className="col-span-2 text-xs text-slate-300">
          Overview (markdown)
          <textarea
            data-testid="wiki-create-overview"
            value={state.overview_md}
            onChange={(e) =>
              setState((s) => ({ ...s, overview_md: e.target.value }))
            }
            rows={3}
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100 font-mono"
          />
          {errorField === 'overview_md' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="col-span-2 text-xs text-slate-300">
          Body (markdown)
          <textarea
            data-testid="wiki-create-body"
            value={state.body_md}
            onChange={(e) =>
              setState((s) => ({ ...s, body_md: e.target.value }))
            }
            rows={8}
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100 font-mono"
          />
          {errorField === 'body_md' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        {topLevelError && (
          <div
            data-testid="wiki-create-top-error"
            className="col-span-2 rounded-sm border border-red-900 bg-red-950/40 px-2 py-1 text-xs text-red-200"
          >
            {topLevelError}
          </div>
        )}
        <div className="col-span-2 flex justify-end gap-2">
          <Link
            to="/wiki"
            className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </Link>
          <button
            type="submit"
            data-testid="wiki-create-submit"
            disabled={mutation.isPending}
            className="rounded-md bg-fishstank-accent px-4 py-1.5 text-sm font-semibold text-fishstank-ink disabled:opacity-50"
          >
            {mutation.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </Shell>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Route 2 — PATCH /wiki/:id/edit   → entity wiki page edit
// ────────────────────────────────────────────────────────────────────────────

interface EditEntityPageState {
  title: string
  slug: string
  status: WikiPageStatus
  overview_md: string
  body_md: string
  is_developing_override: '' | 'true' | 'false'
}

function rowToEditEntityPageState(row: WikiAdminPageRow): EditEntityPageState {
  return {
    title: row.title,
    slug: row.slug,
    status: row.status,
    overview_md: '',
    body_md: '',
    is_developing_override: '',
  }
}

/**
 * Build a delta body containing only the fields actually changed vs the
 * admin-list snapshot we loaded. The worker's whitelist-strict parser
 * only sees fields the editor touched, and `empty_patch` is raised
 * when nothing but `idempotency_key` is on the wire.
 *
 * NB: the admin-list row does NOT carry `overview_md` / `body_md` /
 * `is_developing_override`, so we can't delta those against "prior".
 * Instead we treat a non-empty value in the form as "set to this" and
 * an empty value as "leave untouched". A future CP might load the
 * current row through a dedicated read to enable true delta on long
 * fields.
 */
function buildEntityPageEditDelta(
  initial: EditEntityPageState,
  current: EditEntityPageState,
  idempotencyKey: string,
): WikiEntityPageEditRequest {
  // Use a mutable builder object, then assert to the `readonly`
  // request shape at the end — we construct the payload once and
  // never mutate after.
  const out: {
    idempotency_key: string
    title?: string
    slug?: string
    status?: WikiPageStatus
    overview_md?: string | null
    body_md?: string | null
    is_developing_override?: boolean | null
  } = { idempotency_key: idempotencyKey }
  if (current.title.trim() !== initial.title.trim()) {
    out.title = current.title.trim()
  }
  if (current.slug.trim() !== initial.slug.trim()) {
    out.slug = current.slug.trim()
  }
  if (current.status !== initial.status) {
    out.status = current.status
  }
  // overview_md / body_md / is_developing_override: only send if the
  // editor typed something. Blank means "leave untouched".
  if (current.overview_md !== '') {
    out.overview_md = current.overview_md
  }
  if (current.body_md !== '') {
    out.body_md = current.body_md
  }
  if (current.is_developing_override !== '') {
    out.is_developing_override = current.is_developing_override === 'true'
  }
  return out
}

export function WikiEntityPageEditPage() {
  const nav = useNavigate()
  const params = useParams<{ id: string }>()
  const id = params.id ?? ''

  const query = useQuery({
    queryKey: ['wiki', 'admin', 'list'],
    queryFn: fetchWikiAdminList,
  })

  const row: WikiAdminPageRow | null = useMemo(() => {
    const res = query.data
    if (!res) return null
    if (res.kind !== 'ok' && res.kind !== 'empty') return null
    return res.artifact.body.wiki_pages.find((r) => r.id === id) ?? null
  }, [query.data, id])

  if (query.isLoading) {
    return (
      <Shell provenance={null}>
        <div
          data-testid="wiki-edit-loading"
          className="rounded-md border border-slate-800 bg-fishstank-surface p-6 text-sm text-slate-400"
        >
          Loading wiki page…
        </div>
      </Shell>
    )
  }

  if (!row) {
    return (
      <Shell provenance={null}>
        <div
          data-testid="wiki-edit-missing"
          className="rounded-md border border-red-900 bg-red-950/40 p-6 text-sm text-red-200"
        >
          Wiki page #{id} not found in the admin list.{' '}
          <Link to="/wiki" className="underline">
            Back to list
          </Link>
          .
        </div>
      </Shell>
    )
  }

  return (
    <EditEntityPageForm
      row={row}
      onDone={() => nav('/wiki')}
    />
  )
}

function EditEntityPageForm({
  row,
  onDone,
}: {
  row: WikiAdminPageRow
  onDone: () => void
}) {
  const initial = useMemo(() => rowToEditEntityPageState(row), [row])
  const [state, setState] = useState<EditEntityPageState>(initial)
  const [idempotencyKey] = useState<string>(() => newIdempotencyKey())
  const [validationError, setValidationError] =
    useState<WikiValidationError | null>(null)
  const [topLevelError, setTopLevelError] = useState<string | null>(null)

  const mutation = useMutation<
    WikiMutationFetchResult<WikiMutationReceiptEnvelope<WikiPageRowSnapshot>>,
    Error,
    WikiEntityPageEditRequest
  >({
    mutationFn: (body) => editWikiEntityPage(row.id, { body }),
    onSuccess: (res) => {
      if (res.kind === 'ok') {
        onDone()
      } else if (res.kind === 'validation_error') {
        setValidationError(res.error)
      } else {
        setTopLevelError(topErrorMessage(res))
      }
    },
  })

  const errorField = validationError ? fieldKey(validationError) : null

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setValidationError(null)
    setTopLevelError(null)
    const body = buildEntityPageEditDelta(initial, state, idempotencyKey)
    mutation.mutate(body)
  }

  return (
    <Shell provenance={null}>
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold">
            Edit wiki page #{row.id}
            <span className="ml-2 font-mono text-sm text-slate-500">
              ({row.slug})
            </span>
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Overview and body fields are write-only from the admin list —
            leave blank to keep the current value, or type to overwrite.
            entity_id is not editable.
          </p>
        </div>
        <Link
          to="/wiki"
          data-testid="wiki-edit-cancel"
          className="text-xs text-slate-400 hover:text-slate-200"
        >
          Cancel
        </Link>
      </header>
      <form
        onSubmit={submit}
        data-testid="wiki-edit-form"
        data-page-id={row.id}
        className="grid grid-cols-2 gap-3 rounded-md border border-slate-800 bg-fishstank-surface p-4"
      >
        <label className="col-span-2 text-xs text-slate-300">
          Title
          <input
            type="text"
            data-testid="wiki-edit-title"
            value={state.title}
            onChange={(e) => setState((s) => ({ ...s, title: e.target.value }))}
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
          />
          {errorField === 'title' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="text-xs text-slate-300">
          Slug
          <input
            type="text"
            data-testid="wiki-edit-slug"
            value={state.slug}
            onChange={(e) => setState((s) => ({ ...s, slug: e.target.value }))}
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100 font-mono"
          />
          {errorField === 'slug' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="text-xs text-slate-300">
          Status
          <select
            data-testid="wiki-edit-status"
            value={state.status}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                status: e.target.value as WikiPageStatus,
              }))
            }
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
          >
            {WIKI_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {errorField === 'status' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="text-xs text-slate-300">
          is_developing_override
          <select
            data-testid="wiki-edit-is-developing"
            value={state.is_developing_override}
            onChange={(e) =>
              setState((s) => ({
                ...s,
                is_developing_override:
                  e.target.value as EditEntityPageState['is_developing_override'],
              }))
            }
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100"
          >
            <option value="">(leave untouched)</option>
            <option value="true">force developing</option>
            <option value="false">force ready</option>
          </select>
        </label>
        <label className="col-span-2 text-xs text-slate-300">
          Overview (markdown — leave blank to skip)
          <textarea
            data-testid="wiki-edit-overview"
            value={state.overview_md}
            onChange={(e) =>
              setState((s) => ({ ...s, overview_md: e.target.value }))
            }
            rows={3}
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100 font-mono"
          />
          {errorField === 'overview_md' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="col-span-2 text-xs text-slate-300">
          Body (markdown — leave blank to skip)
          <textarea
            data-testid="wiki-edit-body"
            value={state.body_md}
            onChange={(e) =>
              setState((s) => ({ ...s, body_md: e.target.value }))
            }
            rows={8}
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100 font-mono"
          />
          {errorField === 'body_md' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        {validationError && validationError.code === 'empty_patch' && (
          <div
            data-testid="wiki-edit-no-change"
            className="col-span-2 rounded-sm border border-yellow-900 bg-yellow-950/30 px-2 py-1 text-xs text-yellow-200"
          >
            {validationMessage(validationError)}
          </div>
        )}
        {topLevelError && (
          <div
            data-testid="wiki-edit-top-error"
            className="col-span-2 rounded-sm border border-red-900 bg-red-950/40 px-2 py-1 text-xs text-red-200"
          >
            {topLevelError}
          </div>
        )}
        <div className="col-span-2 flex justify-end gap-2">
          <Link
            to="/wiki"
            className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </Link>
          <button
            type="submit"
            data-testid="wiki-edit-submit"
            data-page-id={row.id}
            disabled={mutation.isPending}
            className="rounded-md bg-fishstank-accent px-4 py-1.5 text-sm font-semibold text-fishstank-ink disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Shell>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Route 3 — PATCH /wiki/arcs/:id/edit   → arc summary_md edit
// ────────────────────────────────────────────────────────────────────────────

interface SummaryEditState {
  summary_md: string
  clear: boolean
}

export function WikiArcSummaryEditPage() {
  const nav = useNavigate()
  const params = useParams<{ id: string }>()
  const id = params.id ?? ''

  const query = useQuery({
    queryKey: ['wiki', 'admin', 'arcs'],
    queryFn: fetchWikiAdminArcs,
  })

  const row: WikiAdminArcRow | null = useMemo(() => {
    const res = query.data
    if (!res) return null
    if (res.kind !== 'ok' && res.kind !== 'empty') return null
    return res.artifact.body.arcs.find((r) => r.id === id) ?? null
  }, [query.data, id])

  if (query.isLoading) {
    return (
      <Shell provenance={null}>
        <div
          data-testid="wiki-arc-edit-loading"
          className="rounded-md border border-slate-800 bg-fishstank-surface p-6 text-sm text-slate-400"
        >
          Loading arc…
        </div>
      </Shell>
    )
  }

  if (!row) {
    return (
      <Shell provenance={null}>
        <div
          data-testid="wiki-arc-edit-missing"
          className="rounded-md border border-red-900 bg-red-950/40 p-6 text-sm text-red-200"
        >
          Arc #{id} not found.{' '}
          <Link to="/wiki" className="underline">
            Back to list
          </Link>
          .
        </div>
      </Shell>
    )
  }

  return <ArcSummaryEditForm row={row} onDone={() => nav('/wiki')} />
}

function ArcSummaryEditForm({
  row,
  onDone,
}: {
  row: WikiAdminArcRow
  onDone: () => void
}) {
  const [state, setState] = useState<SummaryEditState>({
    summary_md: row.summary_md ?? '',
    clear: false,
  })
  const [idempotencyKey] = useState<string>(() => newIdempotencyKey())
  const [validationError, setValidationError] =
    useState<WikiValidationError | null>(null)
  const [topLevelError, setTopLevelError] = useState<string | null>(null)

  const mutation = useMutation<
    WikiMutationFetchResult<
      WikiMutationReceiptEnvelope<WikiArcSummaryRowSnapshot>
    >,
    Error,
    WikiArcSummaryEditRequest
  >({
    mutationFn: (body) => editArcSummary(row.id, { body }),
    onSuccess: (res) => {
      if (res.kind === 'ok') {
        onDone()
      } else if (res.kind === 'validation_error') {
        setValidationError(res.error)
      } else {
        setTopLevelError(topErrorMessage(res))
      }
    },
  })

  const errorField = validationError ? fieldKey(validationError) : null

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setValidationError(null)
    setTopLevelError(null)
    const body: WikiArcSummaryEditRequest = {
      idempotency_key: idempotencyKey,
      summary_md: state.clear ? null : state.summary_md,
    }
    mutation.mutate(body)
  }

  return (
    <Shell provenance={null}>
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold">
            Edit arc summary — {row.title}
            <span className="ml-2 font-mono text-sm text-slate-500">
              ({row.slug})
            </span>
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Narrow edit: only <code className="text-slate-300">summary_md</code>{' '}
            is writable on this form. Title / status / dates / winner are
            out of CP10 scope.
          </p>
        </div>
        <Link
          to="/wiki"
          data-testid="wiki-arc-edit-cancel"
          className="text-xs text-slate-400 hover:text-slate-200"
        >
          Cancel
        </Link>
      </header>
      <form
        onSubmit={submit}
        data-testid="wiki-arc-edit-form"
        data-arc-id={row.id}
        className="grid grid-cols-1 gap-3 rounded-md border border-slate-800 bg-fishstank-surface p-4"
      >
        <label className="text-xs text-slate-300">
          Summary (markdown)
          <textarea
            data-testid="wiki-arc-edit-summary"
            value={state.summary_md}
            disabled={state.clear}
            onChange={(e) =>
              setState((s) => ({ ...s, summary_md: e.target.value }))
            }
            rows={10}
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100 font-mono disabled:opacity-40"
          />
          {errorField === 'summary_md' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <input
            type="checkbox"
            data-testid="wiki-arc-edit-clear"
            checked={state.clear}
            onChange={(e) =>
              setState((s) => ({ ...s, clear: e.target.checked }))
            }
          />
          Clear summary (send <code>null</code>)
        </label>
        {validationError && validationError.code === 'empty_patch' && (
          <div
            data-testid="wiki-arc-edit-no-change"
            className="rounded-sm border border-yellow-900 bg-yellow-950/30 px-2 py-1 text-xs text-yellow-200"
          >
            {validationMessage(validationError)}
          </div>
        )}
        {topLevelError && (
          <div
            data-testid="wiki-arc-edit-top-error"
            className="rounded-sm border border-red-900 bg-red-950/40 px-2 py-1 text-xs text-red-200"
          >
            {topLevelError}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Link
            to="/wiki"
            className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </Link>
          <button
            type="submit"
            data-testid="wiki-arc-edit-submit"
            data-arc-id={row.id}
            disabled={mutation.isPending}
            className="rounded-md bg-fishstank-accent px-4 py-1.5 text-sm font-semibold text-fishstank-ink disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Shell>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Route 4 — PATCH /wiki/challenges/:id/edit   → challenge summary_md edit
// ────────────────────────────────────────────────────────────────────────────

export function WikiChallengeSummaryEditPage() {
  const nav = useNavigate()
  const params = useParams<{ id: string }>()
  const id = params.id ?? ''

  const query = useQuery({
    queryKey: ['wiki', 'admin', 'challenges'],
    queryFn: fetchWikiAdminChallenges,
  })

  const row: WikiAdminChallengeRow | null = useMemo(() => {
    const res = query.data
    if (!res) return null
    if (res.kind !== 'ok' && res.kind !== 'empty') return null
    return res.artifact.body.challenges.find((r) => r.id === id) ?? null
  }, [query.data, id])

  if (query.isLoading) {
    return (
      <Shell provenance={null}>
        <div
          data-testid="wiki-challenge-edit-loading"
          className="rounded-md border border-slate-800 bg-fishstank-surface p-6 text-sm text-slate-400"
        >
          Loading challenge…
        </div>
      </Shell>
    )
  }

  if (!row) {
    return (
      <Shell provenance={null}>
        <div
          data-testid="wiki-challenge-edit-missing"
          className="rounded-md border border-red-900 bg-red-950/40 p-6 text-sm text-red-200"
        >
          Challenge #{id} not found.{' '}
          <Link to="/wiki" className="underline">
            Back to list
          </Link>
          .
        </div>
      </Shell>
    )
  }

  return <ChallengeSummaryEditForm row={row} onDone={() => nav('/wiki')} />
}

function ChallengeSummaryEditForm({
  row,
  onDone,
}: {
  row: WikiAdminChallengeRow
  onDone: () => void
}) {
  const [state, setState] = useState<SummaryEditState>({
    summary_md: row.summary_md ?? '',
    clear: false,
  })
  const [idempotencyKey] = useState<string>(() => newIdempotencyKey())
  const [validationError, setValidationError] =
    useState<WikiValidationError | null>(null)
  const [topLevelError, setTopLevelError] = useState<string | null>(null)

  const mutation = useMutation<
    WikiMutationFetchResult<
      WikiMutationReceiptEnvelope<WikiChallengeSummaryRowSnapshot>
    >,
    Error,
    WikiChallengeSummaryEditRequest
  >({
    mutationFn: (body) => editChallengeSummary(row.id, { body }),
    onSuccess: (res) => {
      if (res.kind === 'ok') {
        onDone()
      } else if (res.kind === 'validation_error') {
        setValidationError(res.error)
      } else {
        setTopLevelError(topErrorMessage(res))
      }
    },
  })

  const errorField = validationError ? fieldKey(validationError) : null

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setValidationError(null)
    setTopLevelError(null)
    const body: WikiChallengeSummaryEditRequest = {
      idempotency_key: idempotencyKey,
      summary_md: state.clear ? null : state.summary_md,
    }
    mutation.mutate(body)
  }

  return (
    <Shell provenance={null}>
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold">
            Edit challenge summary — {row.title}
            {row.arc_slug && (
              <span className="ml-2 font-mono text-sm text-slate-500">
                ({row.arc_slug})
              </span>
            )}
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Narrow edit: only <code className="text-slate-300">summary_md</code>{' '}
            is writable on this form.
          </p>
        </div>
        <Link
          to="/wiki"
          data-testid="wiki-challenge-edit-cancel"
          className="text-xs text-slate-400 hover:text-slate-200"
        >
          Cancel
        </Link>
      </header>
      <form
        onSubmit={submit}
        data-testid="wiki-challenge-edit-form"
        data-challenge-id={row.id}
        className="grid grid-cols-1 gap-3 rounded-md border border-slate-800 bg-fishstank-surface p-4"
      >
        <label className="text-xs text-slate-300">
          Summary (markdown)
          <textarea
            data-testid="wiki-challenge-edit-summary"
            value={state.summary_md}
            disabled={state.clear}
            onChange={(e) =>
              setState((s) => ({ ...s, summary_md: e.target.value }))
            }
            rows={10}
            className="mt-1 block w-full rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-sm text-slate-100 font-mono disabled:opacity-40"
          />
          {errorField === 'summary_md' && validationError && (
            <FieldError error={validationError} />
          )}
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <input
            type="checkbox"
            data-testid="wiki-challenge-edit-clear"
            checked={state.clear}
            onChange={(e) =>
              setState((s) => ({ ...s, clear: e.target.checked }))
            }
          />
          Clear summary (send <code>null</code>)
        </label>
        {validationError && validationError.code === 'empty_patch' && (
          <div
            data-testid="wiki-challenge-edit-no-change"
            className="rounded-sm border border-yellow-900 bg-yellow-950/30 px-2 py-1 text-xs text-yellow-200"
          >
            {validationMessage(validationError)}
          </div>
        )}
        {topLevelError && (
          <div
            data-testid="wiki-challenge-edit-top-error"
            className="rounded-sm border border-red-900 bg-red-950/40 px-2 py-1 text-xs text-red-200"
          >
            {topLevelError}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Link
            to="/wiki"
            className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </Link>
          <button
            type="submit"
            data-testid="wiki-challenge-edit-submit"
            data-challenge-id={row.id}
            disabled={mutation.isPending}
            className="rounded-md bg-fishstank-accent px-4 py-1.5 text-sm font-semibold text-fishstank-ink disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Shell>
  )
}
